import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-sky-50 text-sky-700 border-sky-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/admin/inquiries", { params });
    setItems(data);
  };

  useEffect(() => { load(); }, [status]);

  const setInquiryStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      await load();
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
    } catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/admin/inquiries/${id}`); await load(); if (selected?.id === id) setSelected(null); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, contacted: 0, completed: 0 };
    for (const i of items) c[i.status || "new"] = (c[i.status || "new"] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="inquiries-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[220px]">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, email, company, product…" className="ps-9" data-testid="inq-search-input" />
          </div>
          <Button type="submit" variant="outline" className="rounded-full" data-testid="inq-search-btn">Search</Button>
        </form>
        <div className="flex gap-2">
          {["all", "new", "contacted", "completed"].map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-sm capitalize border ${status === s ? "bg-[#1E5FD9] text-white border-[#1E5FD9]" : "bg-white border-slate-200 text-slate-700 hover:border-[#1E5FD9]"}`}
              data-testid={`inq-filter-${s}`}
            >
              {s} {status === s ? "" : `(${counts[s] || 0})`}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl divide-y max-h-[70vh] overflow-y-auto">
          {items.length === 0 && <p className="p-8 text-center text-slate-500">No inquiries.</p>}
          {items.map(i => {
            const st = i.status || "new";
            return (
              <button
                key={i.id}
                onClick={() => setSelected(i)}
                className={`w-full text-start p-4 hover:bg-slate-50 ${selected?.id === i.id ? "bg-slate-50" : ""}`}
                data-testid={`inquiry-item-${i.id}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{i.name}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[st]}`}>{st}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${i.kind === "quote" ? "bg-[#1E5FD9]/10 text-[#1E5FD9]" : "bg-slate-100 text-slate-600"}`}>{i.kind === "quote" ? "Quote" : "Contact"}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{i.phone}{i.email ? ` • ${i.email}` : ""}{i.company ? ` • ${i.company}` : ""}</p>
                    {i.product_name && <p className="text-xs text-slate-600 mt-0.5 truncate">Product: <b>{i.product_name}</b>{i.quantity ? ` × ${i.quantity}` : ""}</p>}
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">{new Date(i.created_at).toLocaleDateString()}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 h-fit">
          {!selected ? (
            <p className="text-slate-500 text-sm">Select an inquiry to view details.</p>
          ) : (
            <div data-testid="inquiry-detail">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-display text-lg font-semibold">{selected.name}</h3>
                  <p className="text-xs text-slate-500 capitalize">{selected.kind || "contact"} · {new Date(selected.created_at).toLocaleString()}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => del(selected.id)} className="rounded-full text-rose-600 border-rose-200" data-testid={`inq-delete-${selected.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
              <dl className="mt-4 text-sm space-y-1.5">
                <div><dt className="text-slate-500 inline">Phone: </dt><dd className="inline font-medium">{selected.phone || "—"}</dd></div>
                <div><dt className="text-slate-500 inline">Email: </dt><dd className="inline font-medium">{selected.email || "—"}</dd></div>
                <div><dt className="text-slate-500 inline">Company: </dt><dd className="inline font-medium">{selected.company || "—"}</dd></div>
                <div><dt className="text-slate-500 inline">Product: </dt><dd className="inline font-medium">{selected.product_name || "—"}</dd></div>
                <div><dt className="text-slate-500 inline">Quantity: </dt><dd className="inline font-medium">{selected.quantity || "—"}</dd></div>
              </dl>
              {selected.message && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1">Message</p>
                  <p className="text-sm whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{selected.message}</p>
                </div>
              )}
              <div className="mt-5">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {["new", "contacted", "completed"].map(s => (
                    <Button key={s} size="sm" variant={selected.status === s ? "default" : "outline"} className="rounded-full capitalize" onClick={() => setInquiryStatus(selected.id, s)} data-testid={`inq-mark-${s}`}>
                      Mark {s}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
